import { ApplicationRef, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { CustomerModel, UserModel } from '@libs/account/models';
import { AuthService } from '@libs/shared/auth';
import { CapacitorService, EnvironmentService } from '@libs/shared/common';
import { UserService } from '@libs/shared/services';
import { DataLayerPageInfoItem } from '@libs/shop/models';
import {
  catchError,
  combineLatest,
  delay,
  EMPTY,
  filter,
  first,
  firstValueFrom,
  forkJoin,
  from,
  map,
  Observable,
  of,
  switchMap,
  takeUntil,
  timeout,
} from 'rxjs';
import { TrackingGoogleService } from './tracking-google.service';

/** The part of a pageInfo event that only the rendered page can know. */
export type PageInfoData =
  Pick<DataLayerPageInfoItem, 'searchTerm' | 'numSearchResults'>
  & Partial<Pick<DataLayerPageInfoItem, 'productId'>>;

/**
 * A page that holds back the pageInfo event of the navigation it was activated by, until it reports itself as
 * complete. Implemented by `DeferredPageInfoTracking`.
 */
export interface DeferredPageInfo {
  /** Emits once for the current navigation, as soon as the page reports itself as complete. */
  whenCompleted$(): Observable<PageInfoData>;
}

const PAGE_INFO_DATA_TIMEOUT_MS: number = 10000;

const AUTH_SETTLED_TIMEOUT_MS: number = 10000;

const EMPTY_PAGE_INFO_DATA: PageInfoData = { searchTerm: null, numSearchResults: null };

@Injectable({
  providedIn: 'root',
})
export class PageInfoTrackingService {
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly environmentService: EnvironmentService = inject(EnvironmentService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly userService: UserService = inject(UserService);
  private readonly trackingGoogleService: TrackingGoogleService = inject(TrackingGoogleService);
  private readonly router: Router = inject(Router);
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly deferredTrackings: Set<DeferredPageInfo> = new Set<DeferredPageInfo>();
  private readonly authUpdating$: Observable<boolean> = toObservable(this.authService.isUpdating);

  private initialized: boolean = false;

  /**
   * Tracks a pageInfo event for every completed navigation.
   *
   * A page that provides a `DeferredPageInfoTracking` holds its event back until it reports itself as complete, so that
   * a page which first has to load its data, or first has to redirect to its own completed URL, is reported
   * once and with the state it ends up in.
   *
   * Tracking waits one turn, which gives the page time to render, and a page that redirects within that turn
   * cancels the event of the URL it leaves.
   */
  public init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    const navigationStart$: Observable<NavigationStart> = this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
    );

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      switchMap(event => this.trackedNavigation$(event.url).pipe(
        takeUntil(navigationStart$),
        catchError(() => EMPTY),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(item => {
      this.trackingGoogleService.trackPageInfoEvent(item);
    });

    this.trackPageRenderComplete(navigationStart$);
  }

  public track(route: string): void {
    void firstValueFrom(this.userAndCustomer$().pipe(
      map(([user, customer]) => this.getDataLayerPageInfoItem(route, user, customer)),
    )).then(item => {
      this.trackingGoogleService.trackPageInfoEvent(item);
    }).catch(() => undefined);
  }

  /** Registers a page that holds its pageInfo event back. Called by `DeferredPageInfoTracking`, not by pages themselves. */
  public registerDeferredTracking(deferred: DeferredPageInfo): void {
    this.deferredTrackings.add(deferred);
  }

  /** Releases a page registered via `registerDeferredTracking`. Called by `DeferredPageInfoTracking`, not by pages themselves. */
  public releaseDeferredTracking(deferred: DeferredPageInfo): void {
    this.deferredTrackings.delete(deferred);
  }

  /** Waits for the page's initial HTTP requests, not just for routing to finish. */
  private trackPageRenderComplete(navigationStart$: Observable<NavigationStart>): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      switchMap(() => from(this.appRef.whenStable()).pipe(takeUntil(navigationStart$))),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.trackingGoogleService.trackPageRenderCompleteEvent();
    });
  }

  private trackedNavigation$(route: string): Observable<DataLayerPageInfoItem> {
    const pagePath: string = route === '/' ? '/home' : this.safeDecodeRoute(route).toLowerCase();

    return this.pageInfoData$().pipe(
      switchMap(pageInfoData => this.userAndCustomer$().pipe(
        map(([user, customer]) => this.getDataLayerPageInfoItem(pagePath, user, customer, pageInfoData)),
      )),
      first(),
      // Waits one more turn, so that a redirect triggered by the reporting page still cancels this event.
      delay(0),
    );
  }

  private safeDecodeRoute(route: string): string {
    try {
      return decodeURIComponent(route);
    } catch {
      return route;
    }
  }

  /**
   * The data of every page that holds this navigation back, or empty data when no page does.
   * A page that never reports itself falls back to being tracked without its data.
   *
   * Waits one turn before collecting, so that a page rendered right after `NavigationEnd` is included.
   */
  private pageInfoData$(): Observable<PageInfoData> {
    return of(undefined).pipe(
      delay(0),
      switchMap(() => this.deferredPageInfoData$()),
    );
  }

  private deferredPageInfoData$(): Observable<PageInfoData> {
    const deferredTrackings: DeferredPageInfo[] = [...this.deferredTrackings];

    if (!deferredTrackings.length) {
      return of(EMPTY_PAGE_INFO_DATA);
    }

    return forkJoin(deferredTrackings.map(deferred => deferred.whenCompleted$())).pipe(
      map(data => data.reduce<PageInfoData>((merged, item) => ({
        searchTerm: item.searchTerm ?? merged.searchTerm,
        numSearchResults: item.numSearchResults ?? merged.numSearchResults,
        productId: item.productId ?? merged.productId,
      }), EMPTY_PAGE_INFO_DATA)),
      timeout({ first: PAGE_INFO_DATA_TIMEOUT_MS, with: () => of(EMPTY_PAGE_INFO_DATA) }),
    );
  }

  /** Emits once no login is running, so a page reached during a login (e.g. the MSAL redirect target) is reported as logged in. */
  private authSettled$(): Observable<unknown> {
    if (!this.authService.isUpdating()) {
      return of(undefined);
    }

    return this.authUpdating$.pipe(
      filter(updating => !updating),
      first(),
      timeout({ first: AUTH_SETTLED_TIMEOUT_MS, with: () => of(undefined) }),
    );
  }

  private userAndCustomer$(): Observable<[UserModel | undefined, CustomerModel | undefined]> {
    const user$: Observable<UserModel | undefined> = this.authService.isUserAuthorized$().pipe(
      switchMap(authorized => authorized ? this.userService.getCurrentUser() : of(undefined)),
    );

    return this.authSettled$().pipe(
      switchMap(() => combineLatest([user$, this.authService.getCurrentCustomer()])),
      first(),
    );
  }

  private getDataLayerPageInfoItem(route: string, user?: UserModel, customer?: CustomerModel, pageInfoData?: PageInfoData): DataLayerPageInfoItem {
    const pageContent: string = route.length === 0 ? 'home' : route;
    const platform: string = this.capacitorService.getPlatform();
    const environmentType: string = this.environmentService.type();
    const customerId: string = customer?.id ?? 'n/a';
    const ssoId: string = user?.id ?? 'n/a';
    const loginState: 'logged-in' | 'not-logged-in' = user ? 'logged-in' : 'not-logged-in';
    const appVersion: string | null = this.capacitorService.getAppVersion();
    const pzo: string | null = customer?.customerCategorizing?.pzo ?? null;
    const promotionSegment: string = `${customer?.customerCategorizing?.kwa ?? 'none'}/${customer?.customerCategorizing?.zanr ?? 'none'}`;
    const searchTerm: string | null = pageInfoData?.searchTerm ?? null;
    const numSearchResults: number | null = pageInfoData?.numSearchResults ?? null;
    const productId: string | null = pageInfoData?.productId ?? null;

    return {
      event: 'pageInfo',
      appVersion,
      environment: `${platform}.${environmentType}`,
      pageContent,
      customerId,
      ssoId,
      loginState,
      currencyCode: 'EUR',
      pzo,
      promotionSegment,
      searchTerm,
      numSearchResults,
      productId,
    };
  }
}
